import {
  ActionType,
  DayIntervalUnit,
  Issue,
  parseMMDDYYYYToDate,
  TriggerType
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dimensions, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { number, string, object as yupObject } from 'yup';
import { KeyboardSafeView } from '../../components/keyboard/keyboardSafeView';
import { Colors } from '../../themes/colors';
import { SaveFormTop } from '../forms';
import { FormContext } from '../forms/formContext';
import { TopModalBar } from '../modals/topModalBar';
import { IssueReminderDropdown } from './issueReminderDropdown';
import { ReminderAlertInputs, ReminderAlertType } from './types';

type IssueReminderModalProps = {
  defaultValues: any;
  issue: Issue;
  visible: boolean;
  isEditReminder?: boolean;
  hide: () => void;
  submitReminder: (input: any) => Promise<void>;
  deleteReminder?: () => Promise<void>;
  onSuccess?: () => void;
};

export const IssueReminderModal = ({
  defaultValues,
  issue,
  visible,
  isEditReminder,
  hide,
  submitReminder,
  deleteReminder,
  onSuccess,
}: IssueReminderModalProps) => {
  const { t } = useTranslation();
  const { workspace } = issue;
  const MIN_REMINDER_MINUTES = 15;

  const formSchema = yupObject({
    alertType: string().required().min(1, 'Alert type required'),
    inputField: string().required().min(1, 'input required'),
    days: number()
      .when('alertType', {
        is: (val: string) => val === ReminderAlertType.Days,
        then: (schema) => schema.required('Number of days is required').min(1, 'Must be at least 1 day'),
        otherwise: (schema) => schema.optional(),
      }),
    daysStartDate: string()
      .nullable()
      .when('alertType', {
        is: (val: string) => val === ReminderAlertType.Days,
        then: (schema) => schema.required('Start date is required for recurring reminders'),
        otherwise: (schema) => schema.optional(),
      }),
    dayInterval: string()
      .when(['alertType', 'dayIntervalUnit'], {
        is: (alertType: string, dayIntervalUnit: string) => {
          return (
            alertType === ReminderAlertType.DayInterval &&
            dayIntervalUnit === DayIntervalUnit.Minutes
          );
        },
        then: (schema) => schema
          .required('Interval is required')
          .test('min-minutes', 'Must be at least 15 minutes', (value) => {
            const numValue = parseInt(value || '0', 10);
            return numValue >= MIN_REMINDER_MINUTES;
          }),
        otherwise: (schema) => schema.when('alertType', {
          is: (val: string) => val === ReminderAlertType.DayInterval,
          then: (schema) => schema
            .required('Interval is required')
            .test('min-interval', 'Must be at least 1', (value) => {
              const numValue = parseInt(value || '0', 10);
              return numValue >= 1;
            }),
          otherwise: (schema) => schema.optional(),
        }),
      }),
    dayIntervalUnit: string()
      .when('alertType', {
        is: (val: string) => val === ReminderAlertType.DayInterval,
        then: (schema) => schema.required('Interval unit is required'),
        otherwise: (schema) => schema.optional(),
      }),
    onDate: string()
      .when('alertType', {
        is: (val: string) => val === ReminderAlertType.Date,
        then: (schema) => schema.required('Date is required'),
        otherwise: (schema) => schema.optional(),
      }),
    notes: string().optional(),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
    setValue,
  } = formMethods;

  const alertType = useWatch({ control, name: 'alertType' });
  const daysValue = useWatch({ control, name: ReminderAlertInputs.days });
  const dayInterval = useWatch({ control, name: ReminderAlertInputs.dayInterval });
  const dayIntervalUnit = useWatch({ control, name: ReminderAlertInputs.dayIntervalUnit });
  const daysStartDateValue = useWatch({
    control,
    name: ReminderAlertInputs.daysStartDate,
  });
  const onDateValue = useWatch({ control, name: ReminderAlertInputs.onDate });
  const notesValue = useWatch({ control, name: ReminderAlertInputs.notes });

  const updateFormInputField = useCallback((
    inputName: string,
    alertType: ReminderAlertType
  ) => {
    switch (alertType) {
      case ReminderAlertType.Date:
        setValue(inputName, onDateValue || '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case ReminderAlertType.Days:
        setValue(inputName, daysValue ? daysValue.toString() : '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case ReminderAlertType.DayInterval:
        setValue(inputName, dayInterval ? dayInterval.toString() : '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;

      default:
        break;
    }
  }, [setValue, onDateValue, daysValue, dayInterval]);

  useEffect(() => {
    if (!alertType) return;
    updateFormInputField('inputField', alertType as ReminderAlertType);
  }, [alertType, daysValue, onDateValue, updateFormInputField]);

  const onSubmit = async (data: ReminderAlertInputs) => {
    // Ensure inputField is set before submission
    if (alertType) {
      updateFormInputField('inputField', alertType as ReminderAlertType);
    }

    const workspaceId = { id: workspace?.id };
    const trigger = {
      type: TriggerType.Issue,
      config: {
        issueId: issue.id,
        ...(alertType === ReminderAlertType.Date && {
          onDate: parseMMDDYYYYToDate(onDateValue),
        }),
        ...(alertType === ReminderAlertType.Days && {
          every: daysValue,
          startDate: parseMMDDYYYYToDate(daysStartDateValue),
        }),
        ...(alertType === ReminderAlertType.DayInterval && {
          onDate: new Date().toISOString(),
          dayInterval: dayInterval,
          dayIntervalUnit: dayIntervalUnit,
        }),
      },
    };

    const action = {
      type: ActionType.Reminder,
    };

    const newReminderInput = {
      name: notesValue,
      workspace: workspaceId,
      trigger: trigger,
      action: action,
    };

    const editReminderInput = {
      name: notesValue,
      trigger: trigger,
      action: action,
      workspace: workspaceId,
    };
        
    await submitReminder(isEditReminder ? editReminderInput : newReminderInput);
    
    handleDismissModal();
    onSuccess?.();
    
  };

  const handleDismissModal = () => {
    reset();
    hide();
  };

  const isDesktop = () => Platform.OS === 'web';

  const modalChildren = () => (
    <FormContext.Provider value={formMethods}>
      <SaveFormTop
        onSubmit={handleSubmit(onSubmit)}
        isValid={isValid}
        isDirty={isDirty && isValid}
        topBar={TopModalBar}
        title={t('issue.menu.reminder')}
        saveLabel={t('issue.reassign.saveLabel')}
        onCancel={handleDismissModal}
      >
        {isDesktop() ? (
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={[styles.scrollViewContainer, { padding: 20 }]}
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled={true}
          >
            <IssueReminderDropdown alertType={alertType} />
            {isEditReminder && (
              <Pressable
                onPress={deleteReminder}
                hitSlop={5}
                style={styles.deleteReminderButton}
              >
                <Text allowFontScaling={false} style={styles.deleteLabel}>
                  {t('reminders.deleteReminder')}
                </Text>
              </Pressable>
            )}
          </ScrollView>
        ) : (
          <KeyboardSafeView style={styles.container}>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.scrollViewContainer}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled={true}
            >
              <IssueReminderDropdown alertType={alertType} />
              {isEditReminder && (
                <Pressable
                  onPress={deleteReminder}
                  hitSlop={5}
                  style={styles.deleteReminderButton}
                >
                  <Text allowFontScaling={false} style={styles.deleteLabel}>
                    {t('reminders.deleteReminder')}
                  </Text>
                </Pressable>
              )}
            </ScrollView>
          </KeyboardSafeView>
        )}
      </SaveFormTop>
    </FormContext.Provider>
  );

  return isDesktop() ? (
    <Modal
      visible={visible}
      onRequestClose={handleDismissModal}
      transparent={true}
      animationType="fade"
    >
      <View style={styles.webModalBackground}>
        <View style={styles.modalContainer}>
          {modalChildren()}
        </View>
      </View>
    </Modal>
  ) : (
    <Modal
      presentationStyle={'pageSheet'}
      onRequestClose={handleDismissModal}
      onDismiss={handleDismissModal}
      animationType={'slide'}
      visible={visible}
    >
      {modalChildren()}
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: Colors.primaryGrayBackground,
    padding: 20,
  },
  scrollViewContainer: {
    flexGrow: 1,
    paddingBottom: 400, // Increased padding to ensure date picker doesn't hide fields
  },
  deleteReminderButton: {
    height: 25,
    marginTop: 20,
    maxWidth: 180,
  },
  deleteLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.red,
    letterSpacing: -0.25,
  },
  webModalBackground: {
    flex: 1,
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '100%',
    maxWidth: 560,
    minHeight: 461,
    maxHeight: Dimensions.get('window').height * 0.8,
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 12,
    overflow: 'hidden',
    elevation: 10,
  },
});
